import 'react-toastify/dist/ReactToastify.css'
import '../styles/index.css'
import '../styles/globals.scss'

import { HubspotProvider } from '@aaronhayes/react-use-hubspot-form'
import { Analytics } from '@vercel/analytics/react'
import { Amplify, Auth } from 'aws-amplify'
import mixpanel from 'mixpanel-browser'
import { NextPage } from 'next'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'
import React, { Dispatch, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { decodeToken } from 'react-jwt'
import { useDispatch, useSelector } from 'react-redux'
import { CookieConsent } from 'src/components/CookieConsent/CookieConsent'
import * as gtag from 'src/components/global/gtag'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import {
  AMPLIFY_REGION,
  AMPLIFY_USER_POOL_APP_CLIENT_ID,
  AMPLIFY_USER_POOL_ID,
  ENVIRONMENT,
  HUBSPOT_SHELL_SCRIPT_ENABLED,
  MIXPANEL_ENABLED,
  MIXPANEL_PROJECT_TOKEN,
  THEME,
  USE_GOOGLE_ANALYTICS,
} from 'src/config/config'
import { UserInfoDocument } from 'src/graphql/generated/hooks'
import { ensureValidIdToken } from 'src/hooks/tokenRefreshGuard'
import { setAuthToLink, useApolloClient } from 'src/hooks/useApolloClient'
import { useStrapiPreviewRefresh } from 'src/hooks/useStrapiPreviewRefresh'
// import useClearCookiesExceptConsent from 'src/hooks/useClearCookiesExceptConsent'
import { removeAuth, setAuth, setProfile } from 'src/store/actions/auth'
import { wrapper } from 'src/store/setup'
import { AppState } from 'src/store/types'
import { ComponentWrapper } from 'src/templates/ComponentWrapper'

import { getUserInfo } from './api/sso/api'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type NextApplicationPage<P = any, IP = P> = NextPage<P, IP> & {
  requireAuth?: boolean
}

const MyApp = (props: AppProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { Component, pageProps }: { Component: NextApplicationPage; pageProps: any } = props
  const [mounted, setMounted] = useState(false)
  const router = useRouter()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const [token, setToken] = useState<string | undefined>(undefined)
  const [userEmail] = useState<string | undefined>(undefined)
  const hasHydratedRef = useRef(false)
  const apolloClient = useApolloClient() // ✅ Call hook at top level, not inside callbacks

  // Strapi Live Preview: refresh draft data when the admin reports a save
  useStrapiPreviewRefresh()

  if (THEME !== 'ilta') {
    const configure = {
      authenticationFlowType: 'USER_PASSWORD_AUTH',
      Auth: {
        region: AMPLIFY_REGION,
        userPoolId: AMPLIFY_USER_POOL_ID,
        userPoolWebClientId: AMPLIFY_USER_POOL_APP_CLIENT_ID,
      },
    }

    Amplify.configure(configure)
    Auth.configure(configure)
  }

  const getUserByToken = async () => {
    const user = await getUserInfo()

    // If getUserInfo() failed (returned null), it already called logoutAndRedirect()
    // So we should not continue - the redirect will happen
    if (!user) {
      return
    }

    dispatch(
      setProfile({
        username: user?.username,
        email: user?.email,
        isSSOUser: true,
      }),
    )

    const idToken = localStorage.getItem('idToken')
    if (idToken) {
      try {
        // ✅ Use apolloClient from top level - hooks cannot be called inside regular functions
        const res = await apolloClient.query({
          query: UserInfoDocument,
          variables: { token: idToken },
        })

        const userInfo = res.data?.userInfo
        if (userInfo) {
          const setProfileData = setProfile({
            name: userInfo?.username,
            email: userInfo?.email,
            userId: userInfo?.id,
            id: userInfo?.external_id || '',
            companyName: userInfo?.companyName || '',
            companyType: userInfo?.companyType || '',
            isPremium: userInfo?.isPremium || false,
            isVendor: userInfo?.isVendor || false,
            hasVendorCentralAccess: userInfo?.hasVendorCentralAccess || false,
            vendor: userInfo?.vendor?.name || '',
            publishedAt: userInfo?.vendor?.publishedAt || null,
          })
          dispatch(setProfileData)
        }
      } catch (error) {
        // If GraphQL query fails (e.g., auth error), logout
        // eslint-disable-next-line no-console
        console.error('Error fetching user profile:', error)
        // The errorLink in useApolloClient will handle auth errors and trigger logout
        // But if it's a non-auth error, we should still clear the partial profile
        dispatch(removeAuth())
      }
    }
  }

  // ✅ FIX 1: Centralized auth hydration - MOST IMPORTANT
  useEffect(() => {
    if (THEME === 'ilta') return
    // ✅ FIX: Only hydrate once on mount to prevent unnecessary re-renders on navigation
    if (hasHydratedRef.current) return

    const hydrateAuth = async () => {
      try {
        // Don't hydrate if we're on signout page (user is logging out)
        if (router.pathname === '/signout' || router.pathname === '/signout/') {
          hasHydratedRef.current = true
          return
        }

        // Use ensureValidIdToken() which handles both SSO and non-SSO, and refreshes if needed
        const idToken = await ensureValidIdToken()
        if (!idToken) {
          // No valid token - user is not logged in
          // Ensure Redux state is cleared if no token exists (including profile data)
          dispatch(removeAuth())
          hasHydratedRef.current = true
          return
        }

        const _isSSO = localStorage.getItem('sso') === 'true'

        // ✅ FIX: Only dispatch if token actually changed to prevent unnecessary re-renders
        // Use refs to access current values without causing re-renders
        const currentToken = authInfo.idToken || token
        const shouldUpdate = !currentToken || currentToken !== idToken

        if (shouldUpdate) {
          // ✅ Dispatch to Redux - this is what the UI relies on
          dispatch(setAuth({ idToken, isSSOUser: _isSSO }))
          setToken(idToken)
        }

        // For SSO users, also fetch and set profile (only on first hydration or if token changed)
        if (_isSSO && (shouldUpdate || !authInfo.name)) {
          try {
            await getUserByToken()
            // getUserByToken() handles errors internally:
            // - If getUserInfo() fails, it returns early (logout already triggered)
            // - If GraphQL query fails, it dispatches removeAuth()
            // So if we reach here, either profile was set successfully, or auth was cleared
          } catch (error) {
            // If getUserByToken() throws an unexpected error, clear auth
            // eslint-disable-next-line no-console
            console.error('Error in getUserByToken:', error)
            dispatch(removeAuth())
          }
        }

        hasHydratedRef.current = true
      } catch (error) {
        // ✅ FIX 2: Never clear storage silently - handle errors properly
        // eslint-disable-next-line no-console
        console.error('Error hydrating auth:', error)
        // Only clear if it's a critical error (e.g., invalid token format)
        // Don't clear on network errors or transient issues
        const _isSSO = localStorage.getItem('sso') === 'true'
        if (_isSSO) {
          // For SSO, if token is invalid, clear and redirect
          try {
            const storedToken = localStorage.getItem('idToken')
            if (storedToken) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const decoded = decodeToken(storedToken) as any
              if (!decoded?.exp) {
                localStorage.clear()
                dispatch(removeAuth())
                router.push('/signin')
              }
            }
          } catch {
            // Token decode failed - clear everything
            localStorage.clear()
            dispatch(removeAuth())
            router.push('/signin')
          }
        }
        // For non-SSO, Auth.currentAuthenticatedUser() will handle it below
        hasHydratedRef.current = true
      }
    }

    hydrateAuth()
    // ✅ FIX: Include dependencies but use refs to prevent unnecessary re-runs
    // Intentionally empty - we use refs and check router.pathname inside
    // The effect should only run once on mount to prevent unnecessary re-renders
  }, [])

  // ✅ FIX 3: Always catch Auth.currentAuthenticatedUser() errors and fetch profile for non-SSO users
  useLayoutEffect(() => {
    if (THEME === 'ilta') return

    const _isSSO = localStorage.getItem('sso') === 'true'
    if (!_isSSO) {
      Auth.currentAuthenticatedUser()
        .then(async (res) => {
          const idToken = res.signInUserSession.idToken.jwtToken
          setAuthToLink(idToken)
          dispatch(
            setAuth({
              idToken,
            }),
          )
          setToken(idToken)

          // ✅ FIX: Fetch profile data for non-SSO users (including vendor sign-in)
          // This ensures header gets updated with vendor info, isPremium, etc.
          // Only fetch if profile data is missing (check if name is empty, which indicates profile not loaded)
          if (!authInfo.name || authInfo.name === '') {
            try {
              // ✅ Use apolloClient from top level - hooks cannot be called inside callbacks
              const profileRes = await apolloClient.query({
                query: UserInfoDocument,
                variables: { token: idToken },
              })

              const userInfo = profileRes.data?.userInfo
              if (userInfo) {
                dispatch(
                  setProfile({
                    name: userInfo?.username,
                    email: userInfo?.email,
                    userId: userInfo?.id,
                    id: userInfo?.external_id || '',
                    companyName: userInfo?.companyName || '',
                    companyType: userInfo?.companyType || '',
                    isPremium: userInfo?.isPremium || false,
                    isVendor: userInfo?.isVendor || false,
                    hasVendorCentralAccess: userInfo?.hasVendorCentralAccess || false,
                    vendor: userInfo?.vendor?.name || '',
                    publishedAt: userInfo?.vendor?.publishedAt || null,
                  }),
                )
              }
            } catch (error) {
              // Profile fetch failed - log but don't block auth
              // eslint-disable-next-line no-console
              console.error('Error fetching user profile:', error)
            }
          }
        })
        .catch(() => {
          // ✅ User not logged in - do nothing (this is expected for unauthenticated users)
          // Don't clear anything or show errors
        })
    }
  }, [dispatch])

  // Handle same-tab logout: clear Redux when tokens are cleared in current tab
  useEffect(() => {
    if (THEME === 'ilta') return

    const handleSameTabLogout = () => {
      // Skip if we're on signout page (user is logging out)
      if (router.pathname === '/signout' || router.pathname === '/signout/') {
        return
      }

      // Clear Redux state immediately when tokens are cleared in same tab
      // This fixes the issue where storage event doesn't fire in the same tab
      dispatch(removeAuth())
    }

    // Listen for custom logout event (fired when tokens are cleared in same tab)
    window.addEventListener('auth:logout', handleSameTabLogout)

    return () => {
      window.removeEventListener('auth:logout', handleSameTabLogout)
    }
  }, [dispatch, router.pathname])

  // Handle cross-tab login/logout: reload page when token is updated in other tabs (simpler approach)
  useEffect(() => {
    if (THEME === 'ilta') return

    const handleCrossTabTokenUpdate = (event: StorageEvent) => {
      // Skip if we're on signout page (user is logging out)
      if (router.pathname === '/signout' || router.pathname === '/signout/') {
        return
      }

      // Fix #1: Handle localStorage.clear() - key === null means entire storage was cleared
      if (event.key === null) {
        window.location.reload()
        return
      }

      // Only handle storage events (cross-tab), not same-tab events
      // For SSO users: keys are 'idToken' and 'refreshToken'
      // For Non-SSO (Cognito) users: keys are 'CognitoIdentityServiceProvider.${appClientId}.${username}.idToken' etc.
      const isTokenKey =
        event.key === 'idToken' ||
        event.key === 'refreshToken' ||
        event.key?.endsWith('.idToken') ||
        event.key?.endsWith('.refreshToken')

      if (!isTokenKey) {
        return
      }

      // Fix #2: Use event.oldValue and event.newValue for accurate cross-tab state
      // event.oldValue and event.newValue are cross-tab accurate and don't depend on Redux timing
      const newValue = event.newValue
      const oldValue = event.oldValue

      // Handle login (token added): oldValue is null, newValue exists
      // Handle logout (token removed): oldValue exists, newValue is null
      // Handle token refresh/change: oldValue exists, newValue exists but different
      // We reload on all token changes to ensure cross-tab synchronization:
      // - Login: Need to sync new user state and profile
      // - Logout: Need to clear auth state
      // - Token refresh: When tokens expire and are refreshed in another tab, sync the new tokens
      // - Token expiration (refresh failed): Handled as logout (token removed)
      const isLogin = !oldValue && newValue
      const isLogout = oldValue && !newValue
      const isTokenChange = oldValue && newValue && oldValue !== newValue

      if (isLogin || isLogout || isTokenChange) {
        window.location.reload()
      }
    }

    // Listen for cross-tab login/logout events (storage event fires in other tabs only)
    // Note: storage event only fires in OTHER tabs, not the tab that made the change
    window.addEventListener('storage', handleCrossTabTokenUpdate)

    return () => {
      window.removeEventListener('storage', handleCrossTabTokenUpdate)
    }
  }, [router.pathname, authInfo.idToken, token])

  useLayoutEffect(() => {
    const handleRouteChange = (url: string) => {
      if (
        url === '/signin/' &&
        router.asPath !== '/forgot/' &&
        router.asPath !== '/reset/' &&
        router.asPath !== '/signin/' &&
        router.asPath !== '/registration/' &&
        router.asPath !== '/signout/' &&
        router.asPath !== '/' &&
        /\/reset\//.test(router.asPath) === false &&
        /\/sso\/callback\//.test(router.asPath) === false &&
        /\/signup\/confirm\//.test(router.asPath) === false &&
        /\/signin\/sso\//.test(router.asPath) === false &&
        /\/signin\/sso\/\/start\//.test(router.asPath) === false &&
        /\/premium\/invitation\//.test(router.asPath) === false
      ) {
        router.push(`/signin/?redirect=${encodeURIComponent(router.asPath)}`)
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    if (USE_GOOGLE_ANALYTICS) {
      const handleRouteChange = (url: URL) => {
        gtag.pageview(url)
      }
      router.events.on('routeChangeComplete', handleRouteChange)
      return () => {
        router.events.off('routeChangeComplete', handleRouteChange)
      }
    }
  }, [router.events])

  if (!mounted) {
    if (MIXPANEL_ENABLED && MIXPANEL_PROJECT_TOKEN) {
      mixpanel.init(MIXPANEL_PROJECT_TOKEN)
      mixpanel.track('visit_site', {
        email: userEmail || '',
      })
    }
  }

  useEffect(() => {
    if (MIXPANEL_ENABLED) {
      mixpanel.set_group('environment', ENVIRONMENT || 'develop')
      setMounted(true)
    }
  }, [])

  // Check if current page should exclude HubSpot tracking
  const shouldExcludeHubSpot = () => {
    const currentPath = router.asPath
    const excludedPaths = [
      '/signin',
      '/signin/',
      '/forgot',
      '/forgot/',
      '/reset',
      '/reset/',
      '/vendor-signin',
      '/vendor-signin/',
    ]

    return excludedPaths.some((path) => currentPath.startsWith(path))
  }

  return (
    <>
      <div className={`${THEME}`}>
        <HubspotProvider>
          <ComponentWrapper token={token}>
            <Component {...pageProps} />
          </ComponentWrapper>
        </HubspotProvider>
        <ThemeOnly theme="legaltech">
          <CookieConsent />
        </ThemeOnly>
        {router.isPreview && (
          <a
            href={`/api/exit-preview/?url=${encodeURIComponent(router.asPath)}`}
            className="fixed bottom-4 left-4 z-[9999] rounded-full bg-black px-4 py-2 text-sm text-white shadow-lg"
          >
            Draft preview — click to exit
          </a>
        )}
        {HUBSPOT_SHELL_SCRIPT_ENABLED && !shouldExcludeHubSpot() && (
          <>
            <Script
              type="text/javascript"
              id="hs-script-loader"
              async
              defer
              src="//js.hs-scripts.com/21422664.js"
            ></Script>
            <Script type="text/javascript" src="//js.hsforms.net/forms/shell.js"></Script>
          </>
        )}
        {typeof window !== 'undefined' && <Analytics />}
      </div>
    </>
  )
}

export default wrapper.withRedux(MyApp)
